import React, { useState, useEffect, useMemo } from "react";
import UserSidebar from "./UserSidebar";
import Notifications from "../../components/Notifications";
import InviteModal from "../../components/InviteModal";
import AcceptInviteModal from "../../components/AcceptInviteModal";
import XOGameRoom from "../game/XOGameRoom";
import toast from "react-hot-toast";
import XOGameVsComputer from "../game/XOGameVsComputer";
import LeaderboardTabs from "./LeaderboardTabs";
import LeaderboardList from "./LeaderboardList";
import GameHistoryList from "./GameHistoryList";
import { useXOLeaderboards } from "./useXOLeaderboards";
// import { useLeaderboard } from "./useLeaderboard";
import { useNavigate } from "react-router-dom";
import { faker } from "@faker-js/faker";
import { startBotGame } from "../game/startBotGame";

import {
  doc,
  updateDoc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  runTransaction,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import QuickPlayModal from "../game/QuickPlayModal";

// --- Add this at the bottom of your file or in a separate file ---
function WaitingRoomModal({ open, onCancel, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="bg-slate-900 rounded-2xl border-2 border-lime-400 p-8 max-w-xs w-full flex flex-col items-center">
        <div className="text-lime-400 font-bold text-xl mb-2">
          Waiting for Opponent...
        </div>
        <div className="mb-3 text-slate-200">
          You'll be matched automatically when another player enters the same
          bet.
        </div>
        <button
          onClick={onCancel}
          disabled={busy}
          className="w-full bg-slate-700 hover:bg-slate-800 text-white rounded-xl px-6 py-2 mt-2"
        >
          {busy ? "Cancelling..." : "Cancel & Leave Waiting Room"}
        </button>
      </div>
    </div>
  );
}

export default function LobbyPage({
  onlineUsers,
  invites,
  onInvite,
  onAccept,
  onDecline,
  currentGameId,
  user,
  setCurrentGameId,
  busyUserIds = [],
  balance,
}) {
  const { iq, cash } = useXOLeaderboards();

  const realOnline = onlineUsers.filter((u) => u.userId !== user?.uid);

  const fakeUsers = useMemo(() => {
    const need = Math.max(0, 20 - realOnline.length);
    return Array.from({ length: need }, (_, i) => ({
      userId: `bot-${i}-${faker.string.uuid()}`,
      username: faker.internet.userName(),
      avatar: faker.image.avatar(),
      isBot: true,
    }));
  }, [realOnline.length]);

  const combinedUsers = [...realOnline, ...fakeUsers];

  const navigate = useNavigate();

  const [vsComputer, setVsComputer] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null);

  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState(null);

  const [quickPlayModalOpen, setQuickPlayModalOpen] = useState(false);
  const [waitingGameId, setWaitingGameId] = useState(null);

  const [botModalOpen, setBotModalOpen] = useState(false);
  const [botBetAmount, setBotBetAmount] = useState(0.5);

  const [activeGame, setActiveGame] = useState(null);
  const [loadingGame, setLoadingGame] = useState(false);

  const [cancelBusy, setCancelBusy] = useState(false);

  // const iqData = useLeaderboard("iq");
  // const cashData = useLeaderboard("cash");

  // const { iq, cash } = useXOLeaderboards();

  const isWaitingForQuickPlay = !!waitingGameId;

  useEffect(() => {
    if (!currentGameId) {
      setActiveGame(null);
      setLoadingGame(false);
      return;
    }
    setLoadingGame(true);
    const unsub = onSnapshot(doc(db, "games", currentGameId), (snap) => {
      setActiveGame(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoadingGame(false);
    });
    return unsub;
  }, [currentGameId]);

  // useEffect(() => {}, [activeGame]);
  // console.log("currentGameId xo", currentGameId);

  // Show Accept Modal for invites to you
  useEffect(() => {
    if (invites && invites.length > 0) {
      const myPendingInvite = invites
        .filter((inv) => inv.status === "pending" && inv.to === user.uid)
        .sort((a, b) => b.createdAt - a.createdAt)[0]; // latest
      if (
        myPendingInvite &&
        (!pendingInviteData ||
          pendingInviteData.inviteId !== myPendingInvite.inviteId)
      ) {
        setPendingInviteData(myPendingInvite);
        setAcceptModalOpen(true);
      }
    }
  }, [invites, user]);

  const handleStartBotGame = async (betAmount) => {
    // Deduct fee here
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    const currentBalance = userSnap.data().balance || 0;
    if (currentBalance < betAmount) {
      toast.error("Insufficient balance!");
      return;
    }
    await updateDoc(userRef, {
      balance: +(currentBalance - betAmount).toFixed(2),
    });
    setBotBetAmount(betAmount);
    setVsComputer(true);
  };

  // --- Block invite for busy users
  // const canInvite = (u) =>
  const canInvite = (u) => {
    if (!user || !u?.userId) return false; // user not ready or invalid player
    const isBusy = busyUserIds.includes(u.userId);
    const isSelf = u.userId === user.uid;
    return !isBusy && !currentGameId && !isSelf;
  };

  // --- Listen for being matched while waiting ---
  useEffect(() => {
    if (!waitingGameId || !user?.uid) return;
    const q = query(
      collection(db, "games"),
      where("players", "array-contains", user.uid),
      where("status", "==", "active"),
      where("gameType", "==", "xo")
    );
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const game = snap.docs[0];
        setCurrentGameId(game.id);
        setWaitingGameId(null);
        toast.success("Opponent found! Game started.");
        setQuickPlayModalOpen(false);
      }
    });
    return () => unsub();
  }, [waitingGameId, user?.uid]);

  // --- Handle Quick Play / Waiting Room Logic ---
  async function handleQuickPlay(betAmount) {
    if (!user?.uid) return toast.error("Not logged in!");
    if (betAmount < 0.5) return toast.error("Minimum bet is 0.5 USDT!");
    if (betAmount > balance) return toast.error("Insufficient balance!");
    if (isWaitingForQuickPlay) {
      toast.error("You are already in Quick Play waiting room!");
      return;
    }

    // Lock/Deduct bet (atomic)
    const userRef = doc(db, "users", user.uid);
    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(userRef);
      const bal = docSnap.data().balance || 0;
      if (bal < betAmount) throw new Error("Insufficient balance!");
      transaction.update(userRef, { balance: +(bal - betAmount).toFixed(2) });
    });

    // Try to find opponent (atomic check)
    const waitQ = query(
      collection(db, "waiting_games"),
      where("betAmount", "==", betAmount)
    );
    const waitSnap = await getDocs(waitQ);
    const otherPlayerDoc = waitSnap.docs.find((d) => d.data().uid !== user.uid);

    if (otherPlayerDoc) {
      // Found a match!
      const other = otherPlayerDoc.data();
      const players = [
        { userId: user.uid, username: user.displayName, avatar: user.photoURL },
        { userId: other.uid, username: other.username, avatar: other.avatar },
      ];
      const gameRef = await addDoc(collection(db, "games"), {
        gameType: "xo",
        playerX: players[1], // older is X
        playerO: players[0],
        players: [players[1].userId, players[0].userId],
        board: Array(9).fill(null),
        turn: "X",
        status: "active",
        winner: null,
        betAmount: betAmount,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        lastActive: {
          [players[0].userId]: Date.now(),
          [players[1].userId]: Date.now(),
        },
      });
      await deleteDoc(doc(db, "waiting_games", otherPlayerDoc.id));
      setCurrentGameId(gameRef.id);
      setWaitingGameId(null);
      toast.success("Opponent found! Game started.");
    } else {
      // No opponent: add to waiting_games
      const docRef = await addDoc(collection(db, "waiting_games"), {
        uid: user.uid,
        username: user.displayName,
        avatar: user.photoURL,
        betAmount,
        createdAt: Date.now(),
      });
      setWaitingGameId(docRef.id);
      toast("Waiting for opponent...");
      setQuickPlayModalOpen(false);

      // 🧠 Wait 29 seconds, then let fake bot accept if still waiting
      setTimeout(async () => {
        const stillWaitingSnap = await getDoc(docRef);
        if (stillWaitingSnap.exists()) {
          const fakeUser = {
            userId: `bot-${faker.string.uuid()}`,
            username: faker.internet.userName(),
            avatar: faker.image.avatar(),
            isBot: true,
          };

          await deleteDoc(docRef);
          toast.success("🤖 Player accepted your match!");

          startBotGame({
            user,
            botUser: fakeUser,
            betAmount,
            setCurrentGameId,
            fromQuickPlay: true,
          });
        }
      }, 29000);
    }
  }

  // --- Cancel waiting: remove from waiting_games and refund ---
  async function handleCancelWaiting() {
    if (!waitingGameId) return;
    setCancelBusy(true);
    try {
      const docRef = doc(db, "waiting_games", waitingGameId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists() && docSnap.data().uid === user.uid) {
        // Refund user!
        const betAmount = docSnap.data().betAmount || 0.5;
        const userRef = doc(db, "users", user.uid);
        await runTransaction(db, async (transaction) => {
          const userSnap = await transaction.get(userRef);
          const bal = userSnap.data().balance || 0;
          transaction.update(userRef, {
            balance: +(bal + betAmount).toFixed(2),
          });
        });
        await deleteDoc(docRef);
        setWaitingGameId(null);
        toast("Left waiting room and refunded.");
      }
    } catch (err) {
      toast.error("Error cancelling waiting room: " + (err?.message || err));
    }
    setCancelBusy(false);
  }

  const checkPlayerValid = () => {
    if (!user) {
      toast.error("Please log in first");
      navigate("/login");
      return false;
    }

    if (!user.displayName || user.displayName.trim() === "") {
      toast.error("Please set your name in profile before playing");
      navigate("/profile");
      return false;
    }

    return true;
  };

  // --- UI ---
  return (
    <div className="flex min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-lime-950 flex-col md:flex-row md:pt-16">
      {/* Waiting Room Modal */}
      <WaitingRoomModal
        open={!!waitingGameId}
        onCancel={handleCancelWaiting}
        busy={cancelBusy}
      />

      {/* Mobile: Online users bar */}
      <div className="block md:hidden w-full bg-slate-900 px-3 pt-3 pb-2 shadow z-10">
        <div className="flex items-center gap-3 overflow-x-auto">
          <span className="text-lime-400 font-bold mr-2">Online Players:</span>
          {combinedUsers.length === 0 && (
            <span className="text-slate-400">No one online</span>
          )}
          <div className="flex flex-col md:flex-row gap-2">
            {combinedUsers.map((u) => {
              const isBusy = busyUserIds.includes(u.userId);
              return (
                <div
                  key={u.userId}
                  className="flex items-center gap-2 px-2 py-1 rounded-full bg-slate-800"
                >
                  <span className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-white text-sm bg-lime-500 shrink-0">
                    {u.avatar ? (
                      <img
                        src={u.avatar}
                        alt={u.username}
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : u.username ? (
                      u.username[0].toUpperCase()
                    ) : (
                      "U"
                    )}
                  </span>
                  <span className="text-white text-xs">{u.username}</span>
                  <button
                    className={`text-xs font-bold px-2 py-1 rounded-full ml-2
                      ${
                        isBusy
                          ? "bg-gray-400 text-gray-800 cursor-not-allowed"
                          : "bg-lime-400 text-slate-900"
                      }
                    `}
                    style={{ minWidth: 52 }}
                    disabled={isWaitingForQuickPlay || !canInvite(u) || isBusy}
                    onClick={() => {
                      setInviteTarget(u, "xo");
                      setInviteModalOpen(true);
                    }}
                  >
                    {isBusy ? "Busy" : "Invite"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop: Sidebar */}
      <div className="hidden md:block">
        <UserSidebar
          onlineUsers={combinedUsers}
          onInvite={(u) => {
            setInviteTarget(u);
            setInviteModalOpen(true);
          }}
          currentGameId={currentGameId}
          busyUserIds={busyUserIds}
          user={user}
          setInviteTarget={setInviteTarget}
          setInviteModalOpen={setInviteModalOpen}
        />
      </div>

      <InviteModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        maxBalance={balance}
        onSend={async (betAmount) => {
          if (inviteTarget) {
            const inviteResult = await onInvite(inviteTarget, betAmount, "xo");

            if (inviteTarget?.isBot || inviteResult?.isBot) {
              setTimeout(() => {
                toast.success("🤖 Player accepted your invite!");
                startBotGame({
                  user,
                  botUser: inviteTarget,
                  betAmount,
                  setCurrentGameId,
                });
              }, Math.random() * 5000 + 5000);
            }
          }

          setInviteModalOpen(false);
          setInviteTarget(null);
        }}
      />

      <AcceptInviteModal
        open={acceptModalOpen}
        inviter={pendingInviteData?.from}
        betAmount={pendingInviteData?.betAmount}
        onAccept={() => {
          if (pendingInviteData?.inviteId) {
            setAcceptModalOpen(true);
            onAccept(pendingInviteData.inviteId);
            setPendingInviteData(null);
          }
        }}
        onDecline={() => {
          if (pendingInviteData?.inviteId) {
            setAcceptModalOpen(false);
            onDecline(pendingInviteData.inviteId);
            setPendingInviteData(null);
          }
        }}
        onClose={() => {
          setAcceptModalOpen(false);
          setPendingInviteData(null);
        }}
        gameType={pendingInviteData?.gameType}
      />

      <QuickPlayModal
        open={quickPlayModalOpen}
        onClose={() => setQuickPlayModalOpen(false)}
        balance={balance}
        onFindOpponent={handleQuickPlay}
      />

      <InviteModal // You can even reuse your modal!
        open={botModalOpen}
        onClose={() => setBotModalOpen(false)}
        maxBalance={balance}
        onSend={handleStartBotGame}
      />
      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-8">
        {/* <Notifications
          invites={invites}
          onAccept={handleAccept}
          onDecline={onDecline}
        /> */}
        {vsComputer ? (
          <XOGameVsComputer
            onBack={() => setVsComputer(false)}
            user={user}
            betAmount={botBetAmount}
          />
        ) : !currentGameId ? (
          <div className="w-full flex flex-col items-center justify-center gap-5 mt-10">
            <div className="flex flex-col gap-4 items-center w-full max-w-xs">
              <button
                className="
                  w-full
                  flex items-center justify-center
                  bg-gradient-to-r from-lime-400 via-lime-500 to-green-500
                  text-white font-extrabold text-lg
                  px-8 py-4 rounded-2xl shadow-xl
                  hover:scale-105 hover:shadow-2xl transition-all duration-200
                  ring-2 ring-lime-300/30 focus:outline-none
                "
                disabled={isWaitingForQuickPlay}
                onClick={() => {
                  if (!checkPlayerValid()) return;
                  setQuickPlayModalOpen(true);
                }}
                style={{ letterSpacing: "0.03em" }}
              >
                <span className="drop-shadow">🎲 Quick Play</span>
              </button>
              <button
                className="
                  w-full
                  flex items-center justify-center
                  bg-slate-800
                  text-lime-400 font-bold text-lg
                  px-8 py-4 rounded-2xl shadow-lg
                  hover:bg-slate-700 hover:scale-105 hover:shadow-2xl
                  transition-all duration-200
                  ring-1 ring-slate-600/30 focus:outline-none
                "
                // onClick={handlePlayVsComputer}
                onClick={() => {
                  if (!checkPlayerValid()) return;
                  setBotModalOpen(true);
                }}
                style={{ letterSpacing: "0.02em" }}
              >
                <span className="drop-shadow">🤖 Play vs Computer</span>
              </button>
            </div>
            <div className="text-xs text-slate-400 mt-2 italic">
              Minimum bet:{" "}
              <span className="text-lime-400 font-semibold">0.5 USDT</span> per
              game.
            </div>
          </div>
        ) : activeGame && activeGame.gameType === "xo" ? (
          <XOGameRoom
            gameId={currentGameId}
            user={user}
            setCurrentGameId={setCurrentGameId}
            onlineUsers={onlineUsers}
            handleInvite={onInvite}
          />
        ) : (
          <div className="mt-24 text-red-400 font-bold">
            Error: You are already in a game.
          </div>
        )}
        {/* <LeaderboardTabs>
          {(tab) =>
            tab === "iq" ? (
              <LeaderboardList type="iq" data={iq} />
            ) : (
              <LeaderboardList type="cash" data={cash} />
            )
          }
        </LeaderboardTabs> */}

        <GameHistoryList user={user} />
      </main>
    </div>
  );
}
